import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { getRefreshRedirectSignIn } from 'helpers/RouteHelper';

import { clearCache, getAccessToken } from 'helpers/auth.clientless'; // TODO Remove this cookie logic to new AuthHelper after auth.clientless is fully removed
import { isAnonymousAccount, getAccountCookie, removeLegacyAccountCookie } from 'helpers/Account';
import { getDeviceProps } from 'helpers/utils';
import { setSDKAccessToken, setUserAuthData } from 'index/AppActions';
import { differenceInMilliseconds, msToDate, now } from '../helpers/DateTimeHelper';
import {
  AUTH_STATUS,
  FIRST_SCREEN,
  getAuthRedirectUrl,
  getCurrentAuthData,
  getMVPDCookie,
  isPreviewPass,
  isTimestampExpired,
  manageAccountCookies,
  removeMVPDCookie,
  REQUESTOR,
  setMVPDCookie,
  VERSION,
} from './AuthHelper';
import { isMvpdSubscription, isNationEntitled } from '../helpers/ContentHelper';
import { stripAuthProperties } from '../helpers/AuthHelper';
import { apiErrorHandler } from '../helpers/ApiHelper';

const AuthContext = createContext({
  accessToken: null,
  refreshToken: null,
  profileId: null,
  account: null,
  deviceId: null,
  profile: null,
  profileConfig: null,
  auth: null,
  mvpd: null,
  isMVPD: false,
  isAnonymous: true,
});

AuthContext.displayName = 'AuthContext';

const AuthContextProvider = ({ children }) => {
  const { profile, profileConfig, auth } = useSelector(state => state?.app);
  const [isInitialized, setIsInitialized] = useState(false);
  const [preauthRefreshComplete, setPreAuthCheckComplete] = useState(false);
  const [status, setStatus] = useState(AUTH_STATUS.UNINITIALIZED);
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const [authState, setAuthState] = useState({
    accessToken: null,
    refreshToken: null,
    profileId: null,
    account: null,
    anonymousAccount: null,
    deviceId: null,
    profile,
    profileConfig,
    auth,
    mvpd: null,
    isMvpdSubscription: isMvpdSubscription(),
    isAnonymous: !isNationEntitled(),
    isPreviewPass: false,
  });
  const preAuthRef = useRef(false);

  // Initializes and reinitializes
  useEffect(() => {
    const {
      authData: currAccount,
      profileId: currProfileId,
      accessToken: currAccessToken,
      refreshToken: currRefreshToken,
    } = getCurrentAuthData();
    removeLegacyAccountCookie();
    const mvpdCookie = getMVPDCookie() ?? null;
    const mvpdName = mvpdCookie?.mvpd;
    const isPreviewPassMVPD = isPreviewPass(mvpdName);
    const isAnonymous = isAnonymousAccount(currAccount);
    setAuthState(prevAuthState => ({
      ...prevAuthState,
      account: isAnonymous ? null : currAccount,
      anonymousAccount: isAnonymous ? currAccount : null,
      deviceId: currAccount?.deviceId || uuidv4(),
      profileId: currProfileId,
      accessToken: currAccessToken,
      refreshToken: currRefreshToken,
      mvpd: mvpdCookie,
      isMVPD: !!mvpdCookie,
      isAnonymous,
      isPreviewPass: isPreviewPassMVPD,
    }));
    clearCache();

    if (!isInitialized) setIsInitialized(true);
    setStatus(AUTH_STATUS.INITIALIZED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  // Uncomment for debuging
  useEffect(() => {
    console.debug('!! Current Auth Status - ', status);
  }, [status]);

  const updateAuthState = newAccount => {
    const isAnonymous = isAnonymousAccount(newAccount);
    setAuthState(prevAuthState => {
      const prevAccount = isAnonymous ? prevAuthState.anonymousAccount : prevAuthState.account;
      const user = {
        ...prevAccount,
        ...newAccount,
      };
      const currentAuthState = {
        ...prevAuthState,
        accessToken: newAccount?.accessToken,
        account: isAnonymous ? null : user,
        anonymousAccount: isAnonymous ? user : null,
        mvpd: newAccount?.mvpd ? newAccount : null,
        refreshToken: newAccount?.refreshToken,
      };
      manageAccountCookies(currentAuthState);
      return currentAuthState;
    });
  };

  const handleCheckRefresh = useCallback(async () => {
    const { profile, refreshToken, tokenExpiration } = authState;
    const result = await profile?.checkRefreshToken({ tokenExpiration, refreshToken });
    if (!result?.status) {
      updateAuthState(result);
    }
  }, [authState]);

  const handleMVPDLogin = async mvpd => {
    const { model, osName, deviceType } = getDeviceProps();
    const isPreviewPassMvpd = isPreviewPass(authState.mvpd?.mvpd);
    if (isPreviewPassMvpd) {
      // Logout of Preview Pass first if exists
      await handlePreviewPassLogout();
    }
    // This creates the window with the appropriate MVPD
    const mvpdWindow = window.open('', 'mvpdwindow', null, true);
    const redirectUrl = isPreviewPassMvpd ? null : getAuthRedirectUrl();
    const adobeResponse = await authState.auth.getAdobeRegCode({
      deviceId: authState.deviceId,
      deviceType,
      appVersion: VERSION,
      firstScreen: FIRST_SCREEN,
      redirectUrl,
      model,
      osName,
      mvpdId: mvpd,
      requestor: REQUESTOR,
    });
    const { authenticateURL, deviceInfo } = adobeResponse || {};
    if (!authenticateURL) {
      setStatus(AUTH_STATUS.MVPD_DOES_NOT_EXIST);
      return mvpdWindow ? mvpdWindow.close() : null;
    }
    mvpdWindow.location = deviceInfo
      ? `${authenticateURL}&device_info=${deviceInfo}`
      : authenticateURL;

    const account = await handleAuthenticationOnWindowFocus();

    if (mvpdWindow) {
      mvpdWindow.close();
    }

    clearCache();

    return account;
  };

  const handleMVPDLogout = useCallback(async () => {
    const { refreshToken } = authState;
    return new Promise(async (resolve, reject) => {
      try {
        if (refreshToken) {
          const response = await authState?.auth.logoutOfMVPD();
          const authorizationToken = getAccessToken(response);
          const newAccount = stripAuthProperties(response);
          newAccount.mvpd = null;
          delete newAccount.authn_expire;
          setAuthState(prevAuthState => {
            const currentAuthState = {
              ...prevAuthState,
              accessToken: authorizationToken,
              account: newAccount,
              mvpd: null,
              isMVPD: false,
              refreshToken: newAccount?.refreshToken,
            };
            manageAccountCookies(currentAuthState);
            return currentAuthState;
          });
          dispatch(setUserAuthData({ mvpd: null, account: newAccount, isAuthenticated: false }));
          clearCache();
          removeMVPDCookie();
          resolve(newAccount);
        } else {
          console.error('No refresh token to proceed');
          clearCache();
          reject();
        }
      } catch (error) {
        console.error('Failure logout MVPD');
        clearCache();
        reject(error);
      }
    });
  }, [authState, dispatch]);

  const handlePreviewPassLogout = async () => {
    const { account } = authState;
    const isMvpdExpired = isTimestampExpired(account?.authn_expire);

    let logoutAccount = null;
    if (account?.accessToken && !isMvpdExpired) {
      logoutAccount = await handleMVPDLogout();
    }
    return logoutAccount;
  };

  // This checks MVPD auth
  const handleCheckAuthentication = useCallback(
    async (shouldCheckAuth = preauthRefreshComplete) =>
      new Promise(async (resolve, reject) => {
        if (!shouldCheckAuth) resolve(authState?.account);
        try {
          // const { deviceType } = getDeviceProps();
          const { auth, deviceId } = authState;
          let account;
          account = await auth?.checkAdobeAuthNRefresh({
            deviceId,
            requestor: REQUESTOR,
          });
          account = stripAuthProperties(account);
          const newAccount = { ...authState?.account, ...account };
          if (account?.mvpd) {
            setMVPDCookie(account);
          }
          updateAuthState(newAccount);
          dispatch(setSDKAccessToken({ accessToken: newAccount?.accessToken }));
          resolve(newAccount);
        } catch (error) {
          const { account: authAccount, isMvpdSubscription } = authState;
          if (error?.statusCode === 401 || error?.statusCode === 400) {
            try {
              await handleCheckRefresh();
            } catch (e) {
              console.error('Failed to refresh token on 401 or 400 error');
              getRefreshRedirectSignIn();
            }
          }
          if (authAccount) {
            const mvpd = authAccount?.mvpd;
            const tokenTTL = authAccount?.authn_expire || null;
            const tokenDiff = differenceInMilliseconds(msToDate(tokenTTL), now());
            // If User was logged in with PreviewPass and token is still valid, don't remove cookie
            const isValidPreviewPass = isPreviewPass(mvpd)
              ? isPreviewPass(mvpd) && tokenDiff > 0
              : false;

            if (!isValidPreviewPass && !isMvpdSubscription && mvpd && tokenDiff <= 0) {
              try {
                await handleMVPDLogout();
              } catch (e) {
                console.error('Failed to logout MVPD on token expiration');
              }
            }
          }
          reject(error);
        }
      }),
    [authState, handleMVPDLogout, dispatch, handleCheckRefresh, preauthRefreshComplete],
  );

  // This is an infinite loop waiting for auth to return the proper auth
  const handleAuthenticationOnWindowFocus = async () =>
    new Promise(async (resolve, reject) => {
      let documentFocusTimeout;
      const listener = async () => {
        if (!document.hasFocus()) return;
        clearInterval(documentFocusTimeout);
        try {
          const authResult = await handleCheckAuthentication();
          resolve(authResult);
        } catch (e) {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject(null);
        }
      };
      documentFocusTimeout = setInterval(listener, 1000);
    });

  const handleGetEntitlements = useCallback(
    async ({ account }) => {
      try {
        const { deviceType } = getDeviceProps();
        const { deviceId } = authState;
        const result = await authState.auth.getEntitlements({
          deviceId,
          deviceType,
          requestor: REQUESTOR,
          signatureEnabled: true,
        });
        const mvpdData = getMVPDCookie() ?? account ?? {};
        const entitlements = result?.entitlements;
        const preauthorizedResources = entitlements && Object.keys(entitlements);

        if (authState.account && entitlements) {
          const newAccount = {
            ...authState.account,
            ...account,
            preauthorizedResources,
          };
          if (newAccount?.mvpd) {
            setMVPDCookie({ ...mvpdData, preauthorizedResources });
          }
          updateAuthState(newAccount);
        }
        dispatch(setUserAuthData({ preauthorizedResources }));
        setStatus(AUTH_STATUS.ENTITLEMENT_SUCCESS);
        return result;
      } catch (error) {
        setStatus(AUTH_STATUS.ENTITLEMENT_FAILED);
        return null;
      }
    },
    [authState, dispatch],
  );

  const handleAnonymousLogin = async () =>
    new Promise(async (resolve, reject) => {
      const account = getAccountCookie();
      if (isAnonymousAccount(account)) {
        updateAuthState(account);
        clearCache();
        resolve({ data: account });
      } else {
        try {
          const response = await authState?.profile?.loginAnonymously({
            deviceId: authState.deviceId,
          });
          if (response) {
            const newCookie = stripAuthProperties(response);
            clearCache();
            resolve(newCookie);
          }
        } catch (error) {
          reject(new Error(error));
        }
      }
    });

  // This is preauth check when the app starts
  useEffect(() => {
    const handlePreAuth = async () => {
      if (preAuthRef.current) {
        return;
      }

      if (!preauthRefreshComplete && authState.auth && authState.profile && authState.account) {
        try {
          preAuthRef.current = true;
          const newAccount = await handleCheckAuthentication(true);
          await handleGetEntitlements({ account: newAccount });
          setStatus(AUTH_STATUS.PREAUTH_SUCCESS);
        } catch (e) {
          try {
            if (authState?.account && authState?.account?.preauthorizedResources) {
              await handleGetEntitlements({ account: authState?.account });
            }
          } catch (e) {
            apiErrorHandler({ error: e });
            throw e;
          }
          setStatus(AUTH_STATUS.PREAUTH_FAILED);
        }

        setPreAuthCheckComplete(true);
      }
    };
    handlePreAuth();
  }, [
    authState.auth,
    authState.profile,
    authState.account,
    preauthRefreshComplete,
    setPreAuthCheckComplete,
    handleCheckAuthentication,
    handleGetEntitlements,
  ]);

  // Note because of the nature of setStatus being non-async the emitted status will be the last setStatus that is successful
  const contextValue = {
    ...authState,
    emitter: { status },
    isLoading,
    // Login handled by purchase funnel
    // login: () => {
    //   try {
    //     setStatus(AUTH_STATUS.LOGIN_STARTED);
    //     // LOGIN HERE
    //     setStatus(AUTH_STATUS.LOGIN_SUCCESS);
    //   } catch (e) {
    //     setStatus(AUTH_STATUS.LOGIN_FAILED);
    //   }
    //   setRefresh(prevRefresh => !prevRefresh);
    // },
    // Logout handled by redirect on the purchase funnel
    // logout: () => {
    //   try {
    //     setStatus(AUTH_STATUS.LOGOUT_STARTED);
    //     // LOGOUT HERE
    //     setStatus(AUTH_STATUS.LOGOUT_SUCCESS);
    //   } catch (e) {
    //     setStatus(AUTH_STATUS.LOGOUT_FAILED);
    //   }
    //   setRefresh(prevRefresh => !prevRefresh);
    // },
    checkRefresh: async () => {
      // This is for manual refreshing, however the provider itself does refreshing for you
      try {
        setStatus(AUTH_STATUS.REFRESH_STARTED);
        await handleCheckRefresh();
        setStatus(AUTH_STATUS.REFRESH_SUCCESS);
        setRefresh(prevRefresh => !prevRefresh);
        return { success: true };
      } catch (e) {
        setStatus(AUTH_STATUS.REFRESH_FAILED);
        throw e;
      }
    },

    // Must pass what MVPD you want to log in too
    mvpdLogin: async mvpd => {
      try {
        setStatus(AUTH_STATUS.MVPD_LOGIN_STARTED);
        setIsLoading(true);
        const account = await handleMVPDLogin(mvpd);
        setStatus(AUTH_STATUS.MVPD_LOGIN_SUCCESS);
        setIsLoading(false);
        setRefresh(prevRefresh => !prevRefresh);
        return { success: true, account };
      } catch (e) {
        setStatus(AUTH_STATUS.MVPD_LOGIN_FAILED);
        setIsLoading(false);
        throw e;
      }
    },

    mvpdLogout: async () => {
      try {
        setStatus(AUTH_STATUS.MVPD_LOGOUT_STARTED);
        setIsLoading(true);
        const account = await handleMVPDLogout();
        setIsLoading(false);
        setStatus(AUTH_STATUS.MVPD_LOGOUT_SUCCESS);
        await handleGetEntitlements({ account });
        return { success: true, account };
      } catch (e) {
        setStatus(AUTH_STATUS.MVPD_LOGOUT_FAILED);
        setIsLoading(false);
        throw e;
      }
    },
    anonymousLogin: async () => {
      try {
        setStatus(AUTH_STATUS.ANON_LOGIN_STARTED);
        const account = await handleAnonymousLogin();
        setStatus(AUTH_STATUS.ANON_LOGIN_SUCCESS);
        return { success: true, account };
      } catch (e) {
        setStatus(AUTH_STATUS.ANON_LOGIN_FAILED);
        throw e;
      }
    },
    checkAdobeRefresh: async () => {
      try {
        setStatus(AUTH_STATUS.CHECK_AUTH_STARTED);
        const account = await handleCheckAuthentication();
        setStatus(AUTH_STATUS.CHECK_AUTH_SUCCESS);
        return { success: true, account };
      } catch (e) {
        setStatus(AUTH_STATUS.CHECK_AUTH_FAILED);
        throw e;
      }
    },

    getEntitlements: ({ newAccount }) => handleGetEntitlements({ account: newAccount }),
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const withAuth = Component => props => (
  <AuthContext.Consumer>
    {authProps => <Component {...props} {...authProps} />}
  </AuthContext.Consumer>
);

AuthContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default AuthContextProvider;
